import { useBoldCategoryColor } from '@/features/colors/hooks/useBoldCategoryColor';
import { useLightCategoryOptionColors } from '@/features/colors/hooks/useLightCategoryOptionColors';
import type { PageExperience } from '@/features/experiences/types';
import { Box, Button } from '@/ui';

type Props = {
  category: PageExperience['categories'][number];
  // With no filter applied every chip is selected — nothing is being held back.
  isSelected: boolean;
  onToggle: (categoryId: string) => void;
};

// One category in the Key: a pale pill around a full-strength swatch, and a filter
// toggle for that category. Both colors are deliberately independent of the
// experience's colorMode — see useLightCategoryOptionColors.
//
// Filtered out, the chip drops its wash and hollows the swatch, so it still names
// its color but reads as withheld rather than as a different category.
export const ViewKeyChip = ({ category, isSelected, onToggle }: Props) => {
  const { backgroundColor, hoverBackgroundColor, textColor } =
    useLightCategoryOptionColors(category.colorName);
  const swatchColor = useBoldCategoryColor(category.colorName);

  return (
    <Button
      // Selected, the pill darkens its own wash. Filtered out it has no wash to
      // darken, so it takes line.soft — what ahGhost uses for the same job, a
      // transparent control acknowledging a hover.
      _hover={{ bg: isSelected ? hoverBackgroundColor : 'line.soft' }}
      aria-label={category.text ? undefined : 'Unnamed category'}
      aria-pressed={isSelected}
      bg={isSelected ? backgroundColor : 'transparent'}
      borderColor={isSelected ? 'transparent' : 'line.DEFAULT'}
      borderRadius="ah-pill"
      borderWidth="1px"
      color={isSelected ? textColor : 'ink.muted'}
      display="flex"
      fontSize="ah-control"
      fontWeight="medium"
      gap="2"
      h="auto"
      lineHeight="normal"
      maxW="100%"
      minW={0}
      px="3"
      py="1.5"
      variant="unstyled"
      onClick={() => onToggle(category.id)}
    >
      {swatchColor && (
        <Box
          bg={isSelected ? swatchColor : 'transparent'}
          borderColor={swatchColor}
          borderRadius="2px"
          borderWidth="1px"
          boxSize="10px"
          flexShrink={0}
        />
      )}
      {category.text && (
        <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {category.text}
        </Box>
      )}
    </Button>
  );
};
